import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useErrorMessages, useUserSession } from '../../../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../../../models/Logging';
import { canEditKeyFact } from '../../../../../../../../models/UiUtils';
import { useContactKeyDateDeleteMutation } from '../../../../../../../../queries/Contact/useContactKeyDateDeleteMutation';
import { useContactKeyDateUpdateMutation } from '../../../../../../../../queries/Contact/useContactKeyDateUpdateMutation';
import { useContactKeyFactsUpdateMutation } from '../../../../../../../../queries/Contact/useContactKeyFactsUpdateMutation';
import { useGetExternalIntegrationsQuery } from '../../../../../../../../queries/Integrations/useGetExternalIntegrations';
import { THEME } from '../../../../../../../components-v2/Theme/theme';
import { InlineValueEditor } from '../../../../../../../components/entities/InlineValueEditor';
import { Modal } from '../../../../../../../components/Modal';
import { LightBulbIcon } from '../../../../../../../components/svgs/icons/LightBulbIcon';
import { bs } from '../../../../../../../styles/styles';
import { ContactInfoSmartKeyFactsModalContent } from '../../../../../../Contact/components/ContactInfoSmartKeyFactsModal';
import {
	EditKeyFactModalContent,
	type IEditKeyFactPayload,
	isEditableViaKeyFactModal,
} from '../../../../../../Contact/components/EditKeyFactModal';
import { styleSheet } from '../../styles';
import { HouseholdKeyFactItem } from './HouseholdKeyFactItem';
import { KeyFactItem } from './KeyFactItem';

type Props = {
	contact: Api.IContact;
	keyFactToHighlight?: Api.IKeyFact;
	compactLayout?: boolean;
	refetchContact?: () => void;
};

function _KeyFacts({ contact, compactLayout = false, refetchContact }: Props) {
	const errorMessages = useErrorMessages();
	const { logEvent, logApiError } = useEventLogging('KeyFacts');
	const userSession = useUserSession();

	// Fetch v2 integrations
	const { data: externalIntegrations } = useGetExternalIntegrationsQuery({
		impersonationContext: undefined,
	});

	const [keyFacts, setKeyFacts] = React.useState<Api.IKeyFact[] | null>(contact.keyFactsCollection || []);
	const [itemToBeDeletedIndex, setItemToBeDeletedIndex] = React.useState<number | null>(null);
	const [editItemIndex, setEditItemIndex] = React.useState<number>(-1);
	const [editableKeyFact, setEditableKeyFact] = React.useState<Api.IKeyFact | null>(null);
	const [showEditNewItemInput, setShowEditNewItemInput] = React.useState(false);
	const [smartKeyFactModalOpen, setSmartKeyFactModalOpen] = React.useState(false);
	const [keyFactToEdit, setKeyFactToEdit] = React.useState<Api.IKeyFact | null>(null);

	React.useEffect(() => {
		setKeyFacts(contact.keyFactsCollection || []);
	}, [contact.keyFactsCollection]);

	// React Query mutations
	const keyFactsUpdateMutation = useContactKeyFactsUpdateMutation({
		onSuccess: () => {
			setKeyFacts(null);
			if (refetchContact) {
				refetchContact();
			}
		},
		onError: (error: Api.IOperationResultNoValue) => {
			setKeyFacts(null);
			logApiError('UpdateKeyFacts-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const keyDateDeleteMutation = useContactKeyDateDeleteMutation({
		onSuccess: () => {
			// Clear local state and explicitly refetch contact data
			setKeyFacts(null);
			if (refetchContact) {
				refetchContact();
			}
		},
		onError: (error: Api.IOperationResultNoValue) => {
			logApiError('RemoveKeyDate-Error', error);
		},
	});

	const keyDateUpdateMutation = useContactKeyDateUpdateMutation({
		onSuccess: () => {
			setKeyFactToEdit(null);
			refetchContact?.();
		},
		onError: (error: Api.IOperationResultNoValue) => {
			logApiError('UpdateKeyDate-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const onEditKeyFactSaved = (payload: IEditKeyFactPayload) => {
		logEvent('UpdateKeyDate', { id: payload.keyFactId });
		keyDateUpdateMutation.mutate({
			contactId: contact.id,
			keyFactId: payload.keyFactId,
			keyFactMetaData: payload.keyFactMetaData,
			ignoreYear: payload.ignoreYear,
		});
	};

	const onAddKeyFact = (keyFact: Api.IKeyFact) => {
		const newKeyFacts = [...(contact.keyFactsCollection || [])];
		newKeyFacts.push(keyFact);

		logEvent('AddKeyFact');
		// Don't update local state for new key facts - let the server response handle it
		// setKeyFacts(newKeyFacts);
		keyFactsUpdateMutation.mutate({
			contactId: contact.id,
			keyFactsCollection: newKeyFacts,
			contactData: contact,
		});
	};

	const onUpdateKeyFact = (keyFact: Api.IKeyFact) => {
		const newKeyFacts = [...(contact.keyFactsCollection || [])];
		// if the key fact doesn't have a value, this actually just removes it from the collection
		const index = newKeyFacts.findIndex(x => x.id === keyFact.id);
		if (keyFact.value) {
			newKeyFacts.splice(index, 1, keyFact);
		} else {
			newKeyFacts.splice(index, 1);
		}

		logEvent('UpdateKeyFact', { id: keyFact.id });

		keyFactsUpdateMutation.mutate({
			contactId: contact.id,
			keyFactsCollection: newKeyFacts,
			contactData: contact,
		});
	};

	const onEditCanceled = () => {
		setEditableKeyFact(null);
		setEditItemIndex(-1);
		setShowEditNewItemInput(false);
	};

	const onEditTextChanged = (value: string) => {
		if (editableKeyFact) {
			setEditableKeyFact({
				...editableKeyFact,
				value,
			});
		}
	};

	const onItemEditButtonClicked = (item: Api.IKeyFact, index: number) => {
		// Date-based key facts (birthday, anniversary, wedding, financial review, custom kinds) are edited
		// in a dedicated modal so the date + optional name can be edited structurally. Renewal/policy,
		// Turning* milestone variants, and free-text key facts keep the existing inline editor.
		if (isEditableViaKeyFactModal(item)) {
			setKeyFactToEdit(item);
			return;
		}
		setEditableKeyFact({
			id: null,
			value: '',
			...(item || {}),
		});
		setEditItemIndex(index);
		setShowEditNewItemInput(false);
	};

	const onRemoveItem = (item: Api.IKeyFact) => {
		if (item.id) {
			onUpdateKeyFact({
				...item,
				value: null,
			});
		}
		setEditableKeyFact(null);
		setEditItemIndex(-1);
		setShowEditNewItemInput(false);
	};

	const onSaveEdit = () => {
		if (editableKeyFact) {
			setEditableKeyFact({
				...editableKeyFact,
				value: editableKeyFact.value?.trim() || '',
			});
			if (editableKeyFact.id) {
				onUpdateKeyFact(editableKeyFact);
			} else {
				onAddKeyFact(editableKeyFact);
			}
		}
		setEditableKeyFact(null);
		setEditItemIndex(-1);
		setShowEditNewItemInput(false);
	};

	const onSmartKeyFactAdded = () => {
		refetchContact?.();
		setSmartKeyFactModalOpen(false);
	};

	// Check if mutations are in progress for busy state
	const isBusy = keyFactsUpdateMutation.isPending || keyDateDeleteMutation.isPending || keyDateUpdateMutation.isPending;

	const items = keyFacts || contact.keyFactsCollection || [];

	// Memoize spouse contact computation to ensure reactivity
	const spouseContact = React.useMemo(() => {
		return contact.householdMembers?.length
			? contact.householdMembers.find(member => {
					return member.id !== contact.id && member.householdRelationship?.title?.toLowerCase() === 'spouse';
				})
			: null;
	}, [contact.householdMembers, contact.id]);

	return (
		<div className={css(styleSheet.keyFactsContainer)}>
			<LightBulbIcon fill={THEME.colors.text.light} height={24} width={24} />
			<div className={css(styleSheet.keyFactsListContainer)}>
				<ul className={css(styleSheet.keyFactsList)}>
					{items?.map((item, index) => {
						// Supports both v1 (IAccountIntegrations) and v2 (IExternalIntegration[]) integrations
						const canEdit = canEditKeyFact(item, userSession.account.integrations, externalIntegrations);

						return (
							<KeyFactItem
								disableEdit={isBusy}
								key={`keyfact-${item.id}-${index}`}
								isEditing={editItemIndex === index}
								keyFact={item}
								contact={contact}
								onEditClicked={fact => onItemEditButtonClicked(fact, index)}
								onEditCanceled={onEditCanceled}
								onEditTextChanged={onEditTextChanged}
								onSaveEdit={onSaveEdit}
								onDeleteCanceled={() => setItemToBeDeletedIndex(null)}
								onDeleteClicked={() => setItemToBeDeletedIndex(index)}
								editableKeyFact={editableKeyFact}
								onDeleteConfirmed={() => {
									setItemToBeDeletedIndex(null);
									onUpdateKeyFact({
										...items[index],
										value: null,
									});
									onRemoveItem(item);
								}}
								isDeleting={itemToBeDeletedIndex === index}
								canEdit={canEdit}
							/>
						);
					})}
					{spouseContact ? (
						<HouseholdKeyFactItem spouseContact={spouseContact} onHouseholdDeleted={() => refetchContact?.()} />
					) : null}
				</ul>

				<div className={css(bs.flex, bs.itemsCenter, bs.h8, bs.relative)}>
					{showEditNewItemInput ? (
						<InlineValueEditor
							autoComplete='off'
							autoFocus={true}
							compactLayout={compactLayout}
							disabled={isBusy}
							id='entity-info-bullet-list-editor-input'
							onCancelButtonClicked={onEditCanceled}
							onChange={ev => onEditTextChanged(ev.target.value)}
							onKeyDown={ev => {
								if (ev.keyCode === 13) {
									onSaveEdit();
								}
							}}
							onSaveButtonClicked={onSaveEdit}
							value={editableKeyFact?.value || ''}
						/>
					) : (
						<button
							className={`${css(bs.px1)} brand-link`}
							disabled={isBusy || editItemIndex >= 0}
							onClick={() => setSmartKeyFactModalOpen(true)}
						>
							<span>Add a key fact</span>
						</button>
					)}
				</div>
				<Modal
					isOpen={smartKeyFactModalOpen}
					onRequestClose={() => setSmartKeyFactModalOpen(false)}
					useDefaultHeader
					shouldCloseOnOverlayClick
				>
					{smartKeyFactModalOpen ? (
						<ContactInfoSmartKeyFactsModalContent
							compactLayout={compactLayout}
							contact={contact}
							onClose={() => setSmartKeyFactModalOpen(false)}
							onSuccess={onSmartKeyFactAdded}
						/>
					) : null}
				</Modal>
				<Modal
					isOpen={!!keyFactToEdit}
					onRequestClose={() => setKeyFactToEdit(null)}
					useDefaultHeader
					shouldCloseOnOverlayClick
				>
					{keyFactToEdit ? (
						<EditKeyFactModalContent
							compactLayout={compactLayout}
							contact={contact}
							keyFact={keyFactToEdit}
							onClose={() => setKeyFactToEdit(null)}
							onSave={onEditKeyFactSaved}
							isSaving={keyDateUpdateMutation.isPending}
						/>
					) : null}
				</Modal>
			</div>
		</div>
	);
}

export const KeyFacts = observer(_KeyFacts);
